/**
 * Values that differ between deploys but are fixed once a build exists.
 *
 * They are resolved at BUILD time (environment variables read by scripts/prerender.mjs) and
 * embedded into every page, not fetched at runtime. That is what lets a download button be
 * part of the pre-rendered HTML: a visitor with JavaScript off still sees it, and a crawler
 * indexes it. The cost is that changing a URL needs a rebuild — which is a deploy anyway.
 */
export interface SiteConfig {
  /** Where "Open the web app" points. */
  webAppUrl: string;
  /** The Google Play listing for the Android app. */
  androidUrl: string;
  /** The App Store listing for the iPhone app. */
  iosUrl: string;
}

/**
 * The three real destinations. THE LANDING IS ALWAYS PRODUCTION — owner's rule, 2026-08-28.
 *
 * ⚠ READ THIS BEFORE TREATING ANY OF THEM AS A VARIABLE AGAIN. Until this commit the web
 * address was a deploy variable, and the deploy passed `https://signal.cryptico.chat` — the
 * STAGE stand. The public marketing site therefore sent every visitor to stage for months,
 * unnoticed, because a stage URL looks exactly as valid as a production one. The store links
 * were placeholders for the same reason: "a deploy variable, not a code change". A value that
 * only ever had one correct setting is not configuration, it is a constant with a hole in it.
 *
 * So these are the production addresses, and they are the DEFAULT at every level — this file,
 * `landing/Dockerfile`'s ARGs, `landing/deploy/compose-landing.yml` and the deploy workflow all
 * carry the same three. No path through the build, including "the argument was not passed", can
 * produce anything else. `assertProductionUrls` below refuses to let one through anyway.
 */
export const DEFAULT_CONFIG: SiteConfig = {
  webAppUrl: "https://app.cryptico.chat",
  androidUrl: "https://play.google.com/store/apps/details?id=com.snupyv3",
  iosUrl: "https://apps.apple.com/sk/app/cryptico-messenger/id6448121613",
};

/**
 * Substrings that must never reach a link on the public page.
 *
 * The check is a PROHIBITION, not a comparison against DEFAULT_CONFIG, and that difference is
 * the whole point: pinning "equals app.cryptico.chat" would still pass the day someone adds a
 * new build argument and points it at stage. Naming what may not appear catches the stage URL
 * however it arrived — default, build argument, or environment.
 */
export const FORBIDDEN_URL_MARKERS = ["signal.cryptico.chat", "placeholder"] as const;

/**
 * Fails the build if any resolved address is a stage stand or a leftover placeholder.
 *
 * Called by the pre-render, so it runs inside `pnpm build` and therefore inside the Docker
 * image build the deploy uses. A landing that would ship a stage link does not get built at
 * all — the failure lands on the person deploying instead of on a visitor.
 */
export function assertProductionUrls(config: SiteConfig): void {
  const offences = Object.entries(config).flatMap(([field, url]) =>
    FORBIDDEN_URL_MARKERS.filter((marker) => url.includes(marker)).map(
      (marker) => `${field}=${url} contains "${marker}"`,
    ),
  );

  if (offences.length) {
    throw new Error(
      `The landing is always production and must not link to a stage stand or a placeholder. ` +
        `${offences.join("; ")}. Fix the value at its source (build argument, Dockerfile ARG or ` +
        `DEFAULT_CONFIG in src/config.ts) — never by relaxing this check.`,
    );
  }
}

/**
 * Fills in the defaults for anything the build did not supply.
 *
 * The pre-render passes an empty string when its environment variable is unset, and an empty
 * string is not a link — a card built on one leads to the current page. So emptiness is
 * treated as "not configured" and falls back to the production address above, while any real
 * value from the build arguments passes through untouched (and is then checked by
 * `assertProductionUrls`, which is what stops a build argument from reintroducing stage).
 */
export function resolveConfig(config: SiteConfig): SiteConfig {
  return {
    webAppUrl: config.webAppUrl || DEFAULT_CONFIG.webAppUrl,
    androidUrl: config.androidUrl || DEFAULT_CONFIG.androidUrl,
    iosUrl: config.iosUrl || DEFAULT_CONFIG.iosUrl,
  };
}

/** The id the pre-render writes the config block under. */
export const CONFIG_ELEMENT_ID = "site-config";
